import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { SubscriptionFeatures } from '../types';

export function useSubscription() {
  const { profile } = useAuth();
  const [features, setFeatures] = useState<SubscriptionFeatures | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadFeatures() {
      try {
        if (!profile) return;

        const { data, error } = await supabase
          .from('subscription_features')
          .select('*')
          .eq('tier', profile.membership_tier)
          .single();

        if (error) throw error;
        if (mounted) {
          setFeatures(data);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to load subscription features'));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadFeatures();

    return () => {
      mounted = false;
    };
  }, [profile]);

  const canViewContract = async (): Promise<boolean> => {
    if (!profile || !features) return false;

    // Enterprise tier has unlimited views
    if (features.max_monthly_views === -1) return true;

    // Check if we're in a new month and need to reset
    const lastReset = profile.last_view_reset ? new Date(profile.last_view_reset) : null;
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    if (!lastReset || lastReset < startOfMonth) {
      return true; // First view of the month
    }

    return profile.monthly_contract_views < features.max_monthly_views;
  };

  const incrementViews = async (): Promise<void> => {
    if (!profile) return;

    const { error } = await supabase
      .from('profiles')
      .update({
        monthly_contract_views: profile.monthly_contract_views + 1,
        last_view_reset: new Date().toISOString()
      })
      .eq('id', profile.id);

    if (error) throw error;
  };

  return {
    features,
    loading,
    error,
    canViewContract,
    incrementViews,
    viewsRemaining: features 
      ? features.max_monthly_views === -1 
        ? Infinity 
        : features.max_monthly_views - (profile?.monthly_contract_views || 0)
      : 0
  };
}